import React, { useState, useEffect, useMemo } from 'react';
import { Sidebar, Icon, Segment } from 'semantic-ui-react';
import { WidthProvider, Responsive } from 'react-grid-layout';
import { Fab } from 'react-tiny-fab';
import HotelsDropDown from '../../components/shared/HotelsDropDown';
import { widgets as initialWidgets } from './widgets';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import WidgetsList from './widgetsList';
import Panel from '../../components/shared/Panel';
import './styles.less';
import 'react-tiny-fab/dist/styles.css';
import 'react-grid-layout/css/styles.css';
import 'react-grid-layout/node_modules/react-resizable/css/styles.css';
import { useTextResources } from '../../language';
import moment from 'moment';
import { isMobileOnly } from 'mobile-device-detect';

const ResponsiveReactGridLayout = WidthProvider(Responsive);

const getInitialLayouts = () => {
	return initialWidgets.reduce(
		(acc, widget) => {
			acc.lg.push({ ...widget.initialLayout.lg, i: widget.id.toString() });
			acc.md.push({ ...widget.initialLayout.md, i: widget.id.toString() });
			acc.xs.push({ ...widget.initialLayout.xs, i: widget.id.toString() });
			acc.xxs.push({ ...widget.initialLayout.xxs, i: widget.id.toString() });
			return acc;
		},
		{ lg: [], md: [], xs: [], xxs: [] }
	);
};

export const DashBoard = () => {
	const defaultHotelId = window.optima?.user?.CurrentHotel?.Id;
	const [showSidebar, setShowSidebar] = useState(false);
	const [widgets, setWidgets] = useLocalStorageState('Widgets', initialWidgets);
	const [hotelIds, setHotelIds] = useState(
		defaultHotelId ? [defaultHotelId] : []
	);
	const [layouts, setLayouts] = useLocalStorageState(
		'DashboardLayouts',
		getInitialLayouts()
	);
	const text = useTextResources();
	var width = screen.width;

	const derivedWidgets = useMemo(
		() =>
			initialWidgets.map((initialWidget) => {
				const widget = widgets.find((widget) => widget.id === initialWidget.id);
				return { ...initialWidget, isEnabled: widget?.isEnabled };
			}),
		[widgets]
	);

	useEffect(() => {
		window.optima.commands.registerCustomToolbarComponent(() => (
			<HotelsDropDown
				key='HotelsDropDown'
				multiple
				placeholder={text("All")}
				value={hotelIds}
				onChange={setHotelIds}
				style={{ minWidth: '120px', verticalAlign: 'middle' }}
			/>
		));
		window.optima.commands.registerCustomToolbarComponent(() => (
			<div
      key="datestamp"
				style={{
					display: 'inline-block',
					float: 'right',
					marginTop: '10px',
					marginRight: '15px',
				}}
			>
				Date: {moment().format('DD-MM-YYYY')}
			</div>
		));
	}, []);

	const onLayoutChange = (layout, layouts) => {
		setLayouts(layouts);
	};

	const toggleWidgetHandler = (widgetId) => {
		const widget = widgets.find((widget) => widget.id === widgetId);
		widget.isEnabled = !widget.isEnabled;
		setWidgets([...widgets]);
	};

	const onResetLayoutsHandler = () => {
		setLayouts(getInitialLayouts());
	};

	function getCurrentBreakpoint(width) {
		if (width >= 1200) return 'lg';
		else if (width >= 996) return 'md';
		else if (width >= 768) return 'sm';
		else if (width >= 480) return 'xs';
		else return 'xxs';
	}

	return (
		<>
			<Sidebar.Pushable style={{ width: '100%', display: 'flex' }}>
				<Sidebar
					as={Segment}
					animation='overlay'
					visible={showSidebar}
					onHide={(e) => {
						e.stopPropagation();
						setShowSidebar(false);
					}}
					width={isMobileOnly ? 'wide' : 'very wide'}
				>
					<WidgetsList
						widgets={derivedWidgets}
						onToggleWidget={toggleWidgetHandler}
						onResetLayouts={onResetLayoutsHandler}
					/>
				</Sidebar>
				<Sidebar.Pusher style={{ flex: 1, overflow: 'auto' }}>
					<ResponsiveReactGridLayout
						className='layout'
						layouts={layouts}
						breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
						cols={{ lg: 36, md: 30, sm: 18, xs: 12, xxs: 6 }}
						rowHeight={32}
						draggableHandle='.ui.top.attached.message'
						margin={[20, 20]}
						verticalCompact={true}
						onLayoutChange={onLayoutChange}
						resizeHandles={['se']}
					>
						{derivedWidgets
							.filter(
								(widget) =>
									widget.isEnabled &&
									window.optima.user?.ActionIDs.includes(widget.actionId)
							)
							.map((widget) => {
								const { initialLayout, component: ComponentTag } = widget;
								return (
									<div
										key={widget.id}
										data-grid={initialLayout[getCurrentBreakpoint(width)]}
									>
										{/* data-grid as default initial sizes for the case we disabled and re-enabled the CMP */}
										<span
											onClick={() => toggleWidgetHandler(widget.id)}
											style={{
												position: 'absolute',
												right: '10px',
												top: '25px',
												cursor: 'pointer',
												zIndex: '1',
											}}
										>
											x
										</span>
										<Panel header={text(widget.title)} overflow='hidden'>
											<ComponentTag HotelIds={hotelIds} />
										</Panel>
									</div>
								);
							})}
					</ResponsiveReactGridLayout>
				</Sidebar.Pusher>
			</Sidebar.Pushable>
			<Fab
				alwaysShowTitle={false}
				style={{ position: 'fixed', right: 15, bottom: 0 }}
				icon={<Icon fitted name='plus' />}
				mainButtonStyles={{ backgroundColor: '#01a7bd' }}
				onClick={() => setShowSidebar(!showSidebar)}
				text='Add/Remove Widgets'
			></Fab>
		</>
	);
};
